import { useNavigate, useLocation } from 'react-router-dom';
import type { MobileTab } from '@/hooks/useModuleConfig';

export interface MobileTabBarProps {
  tabs: MobileTab[];
}

/**
 * Sticky bottom tab bar for the handful of destinations a Box Pro moves between
 * constantly — Calendar, Locations, Members, Pulse.
 *
 * Shown only on first-level routes. On a detail, create or edit screen it would
 * compete with that page's own footer actions (TeeSectionFooter is also pinned
 * to the bottom) and offer an easy way to lose unsaved work; the back row above
 * is the right affordance there. The caller decides — see `isFirstLevelRoute`.
 */
export function MobileTabBar({ tabs }: MobileTabBarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  if (tabs.length === 0) return null;

  // Longest-prefix wins so exactly one tab lights up. /locations and
  // /locations/:id/view both belong to Locations, and a bare `startsWith`
  // would otherwise light several tabs at once.
  const activeHref = tabs
    .filter(
      (tab) =>
        location.pathname === tab.href || location.pathname.startsWith(tab.href + '/'),
    )
    .reduce<string | null>(
      (best, tab) => (best === null || tab.href.length > best.length ? tab.href : best),
      null,
    );

  return (
    <nav
      // Reads as a surface sitting above the page rather than a strip appended
      // to it: an upward shadow, a surface fill lighter than the page canvas,
      // and a hairline top border. Without the shadow it blended into the cream
      // background and looked like content.
      className="md:hidden flex-shrink-0 flex items-stretch border-t border-[var(--border-default)] bg-[var(--background-page-content)] shadow-[0_-6px_20px_-4px_rgba(12,61,45,0.14)] pb-[env(safe-area-inset-bottom,0px)] relative z-30"
      aria-label="Primary"
    >
      {tabs.map((tab) => {
        const isActive = tab.href === activeHref;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => navigate(tab.href)}
            aria-current={isActive ? 'page' : undefined}
            className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1 pt-2 pb-1.5 focus:outline-none"
          >
            {/* The icon sits in its own pill so the active state has a shape to
                fill — a colour change alone is easy to miss at this size. The
                active pill lifts slightly to reinforce it. */}
            <span
              className={`flex items-center justify-center h-8 w-12 rounded-full transition-all duration-150 ${
                isActive
                  ? 'bg-[var(--color-forest)] text-[var(--text-on-forest)] -translate-y-0.5 shadow-[0_2px_8px_-1px_rgba(12,61,45,0.35)]'
                  : 'text-[var(--text-content-sub-title)]'
              }`}
            >
              {tab.icon}
            </span>
            <span
              className={`text-[11px] leading-tight truncate max-w-full px-1 transition-colors ${
                isActive
                  ? 'font-semibold text-[var(--color-forest)]'
                  : 'font-medium text-[var(--text-content-sub-title)]'
              }`}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
